
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Product } from "@/types";
import { toast } from "sonner";
import { ProductBasicInfo } from "./ProductBasicInfo";
import { ImageUploader } from "./ImageUploader";
import { LicenseDatePicker } from "./LicenseDatePicker";
import { validateProductForm } from "./formValidation";

interface ProductFormProps {
  product?: Product | null;
  onSave: (product: Omit<Product, "id" | "status" | "createdAt" | "createdBy"> | Product) => Promise<void>;
  isSubmitting?: boolean;
  onClose?: () => void;
}

const ProductForm = ({ product, onSave, isSubmitting = false, onClose }: ProductFormProps) => {
  const [name, setName] = useState("");
  const [ipAddress, setIpAddress] = useState("");
  const [description, setDescription] = useState("");
  const [primaryEmail, setPrimaryEmail] = useState("");
  const [secondaryEmails, setSecondaryEmails] = useState<string[]>([]);
  const [imageUrls, setImageUrls] = useState<string[]>([""]);
  const [startDate, setStartDate] = useState<Date | undefined>(new Date());
  const [endDate, setEndDate] = useState<Date | undefined>(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
  
  // Reset form when product changes
  useEffect(() => {
    if (product) {
      setName(product.name);
      setIpAddress(product.ipAddress);
      setDescription(product.description || "");
      setPrimaryEmail(product.primaryEmail || "");
      
      // Parse secondary emails from comma-separated string
      const secondaryList = product.secondaryEmails ? 
        product.secondaryEmails.split(',').filter(email => email.trim()) : [];
      setSecondaryEmails(secondaryList);
      
      // Parse comma-separated URLs into array
      const urls = product.imagePath ? product.imagePath.split(',').filter(Boolean) : [""];
      setImageUrls(urls.length ? urls : [""]);
      setStartDate(new Date(product.licenseStartDate));
      setEndDate(new Date(product.expirationDate));
    } else {
      // Initialize with default values for new product
      setName("");
      setIpAddress("");
      setDescription("");
      setPrimaryEmail("");
      setSecondaryEmails([]);
      setImageUrls([""]);
      setStartDate(new Date());
      setEndDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
    }
  }, [product]);

  const handleSubmit = async () => {
    // Nettoyage des emails secondaires avant validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const filteredSecondaryEmails = secondaryEmails
      .map(email => email.trim())
      .filter(email => !!email && emailRegex.test(email));

    const formData = {
      name,
      ipAddress,
      description,
      primaryEmail,
      secondaryEmails: filteredSecondaryEmails, // correction ici
      imageUrls,
      startDate,
      endDate
    };

    if (!validateProductForm(formData)) return;

    try {
      // Filter out empty image URLs and join to comma-separated string
      const filteredImageUrls = imageUrls.filter(url => url.trim()).join(',');

      const productData = {
        ...product,
        name,
        ipAddress,
        description,
        primaryEmail,
        secondaryEmails: filteredSecondaryEmails.join(','), // correction ici
        imagePath: filteredImageUrls || "",
        licenseStartDate: startDate?.toISOString() || new Date().toISOString(),
        expirationDate: endDate?.toISOString() || new Date().toISOString(),
      };

      await onSave(productData);

      if (!product) {
        // Clear form after successful addition
        setName("");
        setIpAddress("");
        setDescription("");
        setPrimaryEmail("");
        setSecondaryEmails([]);
        setImageUrls([""]);
        setStartDate(new Date());
        setEndDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
        toast.success("Produit ajouté avec succès");
      } else {
        toast.success("Produit mis à jour avec succès");
      }

      if (onClose) onClose();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement du produit:", error);
      toast.error(`Une erreur s'est produite: ${error}`);
    }
  };

  return (
    <div className="py-4 space-y-4">
      <ProductBasicInfo
        name={name}
        ipAddress={ipAddress}
        description={description}
        primaryEmail={primaryEmail}
        secondaryEmails={secondaryEmails}
        isSubmitting={isSubmitting}
        onNameChange={setName}
        onIpAddressChange={setIpAddress}
        onDescriptionChange={setDescription}
        onPrimaryEmailChange={setPrimaryEmail}
        onSecondaryEmailsChange={setSecondaryEmails}
      />

      <ImageUploader
        imageUrls={imageUrls}
        isSubmitting={isSubmitting}
        onImageUrlsChange={setImageUrls}
      />

      <LicenseDatePicker
        startDate={startDate}
        endDate={endDate}
        isSubmitting={isSubmitting}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
      />

      <div className="flex justify-end space-x-2 pt-4">
        {onClose && (
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Annuler
          </Button>
        )}
        <Button onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? "Enregistrement..." : product ? "Mettre à jour" : "Ajouter"}
        </Button>
      </div>
    </div>
  );
};

export default ProductForm;
